import { CommonModule } from '@angular/common';
import { inject, NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import { EffectsModule } from '@ngrx/effects';

import { ChatContextRegistry } from '@blaze-dashboard/ai-chat-data-access';
import { ApiBuilderModule } from '@blaze-dashboard/api-builder-feature';
import { BlazeTableModule } from '@blaze-dashboard/blaze-table-feature';
import { FormBuilderModule } from '@blaze-dashboard/form-builder-feature';
import {
  CopyConnectorConfigEnvButtonComponent,
  EnvironmentIndicatorComponent,
  FormatValueModule,
  SharedModule,
} from '@blaze-shared/shared-feature';
import {
  FilterInputComponent,
  LoadingIndicatorComponent,
  PanelComponent,
  ScrollerComponent,
} from '@blaze-shared/shared-ui';

import { DataConnectorsEffects } from './+state/data-connectors.effects';
import { ConnectorMenuItemComponent } from './connector-menu-item/connector-menu-item.component';
import { DataConnectorDatasetsComponent } from './data-connector-datasets/data-connector-datasets.component';
import { DataConnectorDetailsComponent } from './data-connector-details/data-connector-details.component';
import { DataConnectorDialogComponent } from './data-connector-dialog/data-connector-dialog.component';
import { DataConnectorsMenuComponent } from './data-connectors-menu/data-connectors-menu.component';
import { DatasetGroupComponent } from './dataset-group/dataset-group.component';
import { DatasetPreviewComponent } from './dataset-preview/dataset-preview.component';
import { DatasetToggleComponent } from './dataset-toggle/dataset-toggle.component';
import { AircallConnectorDialogComponent } from './dialogs/aircall-connector-dialog/aircall-connector-dialog.component';
import { AirtableConnectorDialogComponent } from './dialogs/airtable-connector-dialog/airtable-connector-dialog.component';
import { BlazeTableDialogComponent } from './dialogs/blaze-table-dialog/blaze-table-dialog.component';
import { BlazeTableFilterDialogComponent } from './dialogs/blaze-table-dialog/blaze-table-filter-dialog/blaze-table-filter-dialog.component';
import { BlazeTableIndexesDialogComponent } from './dialogs/blaze-table-dialog/blaze-table-indexes-dialog/blaze-table-indexes-dialog.component';
import { CalConnectorDialogComponent } from './dialogs/cal-connector-dialog/cal-connector-dialog.component';
import { CalendlyConnectorDialogComponent } from './dialogs/calendly-connector-dialog/calendly-connector-dialog.component';
import { DropboxConnectorDialogComponent } from './dialogs/dropbox-connector-dialog/dropbox-connector-dialog.component';
import { DropboxSignConnectorDialogComponent } from './dialogs/dropbox-sign-connector-dialog/dropbox-sign-connector-dialog.component';
import { GenericOauthConnectorDialogComponent } from './dialogs/generic-oauth-connector-dialog/generic-oauth-connector-dialog.component';
import { GoogleAnalyticsConnectorDialogComponent } from './dialogs/google-analytics-connector-dialog/google-analytics-connector-dialog.component';
import { GoogleCalendarConnectorDialogComponent } from './dialogs/google-calendar-connector-dialog/google-calendar-connector-dialog.component';
import { GoogleMailConnectorDialogComponent } from './dialogs/google-mail-connector-dialog/google-mail-connector-dialog.component';
import { GoogleSheetsConnectorDialogComponent } from './dialogs/google-sheets-connector-dialog/google-sheets-connector-dialog.component';
import { InBodyConnectorDialogComponent } from './dialogs/in-body-connector-dialog/in-body-connector-dialog.component';
import { LifefileConnectorDialogComponent } from './dialogs/lifefile-connector-dialog/lifefile-connector-dialog.component';
import { LinkedBlazeTableDialogComponent } from './dialogs/linked-blaze-table-dialog/linked-blaze-table-dialog.component';
import { MicrosoftTeamsConnectorDialogComponent } from './dialogs/microsoft-teams-connector-dialog/microsoft-teams-connector-dialog.component';
import { OneDriveConnectorDialogComponent } from './dialogs/one-drive-connector-dialog/one-drive-connector-dialog.component';
import { OpenAIConnectorDialogComponent } from './dialogs/openai-connector-dialog/openai-connector-dialog.component';
import { OpenAIConnectorFunctionsContainerComponent } from './dialogs/openai-connector-dialog/openai-connector-functions-container/openai-connector-functions-container.component';
import { OpenAIConnectorOutputParametersComponent } from './dialogs/openai-connector-dialog/openai-connector-output-parameters/openai-connector-output-parameters.component';
import { ScriptSureConnectorDialogComponent } from './dialogs/scriptsure-connector-dialog/scriptsure-connector-dialog.component';
import { ShopifyConnectorDialogComponent } from './dialogs/shopify-connector-dialog/shopify-connector-dialog.component';
import { StripeConnectorDialogComponent } from './dialogs/stripe-connector-dialog/stripe-connector-dialog.component';
import { TwilioConnectorDialogComponent } from './dialogs/twilio-connector-dialog/twilio-connector-dialog.component';
import { TypeformConnectorDialogComponent } from './dialogs/typeform-connector-dialog/typeform-connector-dialog.component';
import { MappingRowComponent } from './mapping-row/mapping-row.component';
import { TableEditorChatContextProvider } from './services/table-editor-chat-context.provider';

const STANDALONE_COMPONENTS = [
  AircallConnectorDialogComponent,
  AirtableConnectorDialogComponent,
  BlazeTableDialogComponent,
  BlazeTableFilterDialogComponent,
  BlazeTableIndexesDialogComponent,
  CalConnectorDialogComponent,
  CalendlyConnectorDialogComponent,
  ConnectorMenuItemComponent,
  CopyConnectorConfigEnvButtonComponent,
  DataConnectorDatasetsComponent,
  DataConnectorDetailsComponent,
  DataConnectorDialogComponent,
  DataConnectorsMenuComponent,
  DatasetGroupComponent,
  DatasetPreviewComponent,
  DatasetToggleComponent,
  DropboxConnectorDialogComponent,
  DropboxSignConnectorDialogComponent,
  EnvironmentIndicatorComponent,
  FilterInputComponent,
  GenericOauthConnectorDialogComponent,
  GoogleAnalyticsConnectorDialogComponent,
  GoogleCalendarConnectorDialogComponent,
  GoogleMailConnectorDialogComponent,
  GoogleSheetsConnectorDialogComponent,
  InBodyConnectorDialogComponent,
  LifefileConnectorDialogComponent,
  LinkedBlazeTableDialogComponent,
  LoadingIndicatorComponent,
  MappingRowComponent,
  MicrosoftTeamsConnectorDialogComponent,
  OneDriveConnectorDialogComponent,
  OpenAIConnectorDialogComponent,
  OpenAIConnectorFunctionsContainerComponent,
  OpenAIConnectorOutputParametersComponent,
  PanelComponent,
  ScriptSureConnectorDialogComponent,
  ScrollerComponent,
  ShopifyConnectorDialogComponent,
  StripeConnectorDialogComponent,
  TwilioConnectorDialogComponent,
  TypeformConnectorDialogComponent,
];

@NgModule({
  imports: [
    CommonModule,
    RouterModule,
    SharedModule,
    FormBuilderModule,
    FormatValueModule,
    BlazeTableModule,
    ApiBuilderModule,

    EffectsModule.forFeature([DataConnectorsEffects]),

    ...STANDALONE_COMPONENTS,
  ],
})
export class DataConnectorsModule {
  private readonly chatContextRegistry = inject(ChatContextRegistry);
  private readonly tableEditorChatContextProvider = inject(TableEditorChatContextProvider);

  constructor() {
    this.chatContextRegistry.register(this.tableEditorChatContextProvider);
  }
}
